import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { usePermissions } from '../../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../../application/context/PermissionsContext/permissionTypes';
import { EditLessonModal } from './EditLessonModal';

interface Lesson {
  id: string;
  name: string;
  description: string;
  youTubeURL: string | null;
  url: string;
  urlType: string;
  order: number;
  imageName: string;
  imageType: string;
  posterImageName: string | null;
  category: string;
  createdAt: string;
  videoURL?: string;
}

interface LessonCardProps {
  lesson: Lesson;
  display: 'grid' | 'list';
  refresh: () => void;
  snackbar: (message: string, severity: string) => void;
}

export function LessonCard({
  lesson,
  display,
  refresh,
  snackbar,
}: LessonCardProps) {
  const { checkPermission } = usePermissions();
  const canManage = checkPermission(PermissionKeys.Other_ManageTrainingVideos);
  const navigate = useNavigate();
  const [openEditor, setOpenEditor] = useState(false);

  const thumbnailStyle = useMemo(() => {
    if (lesson.posterImageName) {
      return { backgroundImage: `url(${lesson.posterImageName})` };
    }
    if (lesson.imageType === 'pdf') {
      return {
        backgroundImage: 'url(/images/pdf-icon.jpg)',
        backgroundSize: '60%',
        backgroundColor: '#fff',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
    return {
      backgroundImage:
        'linear-gradient(to top right, white, hsl(var(--primary)))',
    };
  }, [lesson.posterImageName, lesson.imageType]);

  const descriptionShort = useMemo(() => {
    if (lesson.description && lesson.description.length > 80) {
      return `${lesson.description.slice(0, 80)}...`;
    }
    return lesson.description || '';
  }, [lesson.description]);

  const handleClick = () => {
    navigate(`/v2/training/${lesson.category}/${lesson.id}`);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenEditor(true);
  };

  if (display === 'list') {
    return (
      <>
        <div
          onClick={handleClick}
          className="flex items-center p-4 rounded-lg border border-border bg-card shadow-sm cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5"
        >
          <div
            style={thumbnailStyle}
            className="h-[104px] w-[184px] rounded-lg bg-cover bg-center shrink-0"
          />
          <div className="ml-4 flex-1 min-w-0 relative pr-10">
            <p className="font-semibold text-foreground truncate">
              {lesson.name}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {descriptionShort}
            </p>
          </div>
          {canManage && (
            <button
              onClick={handleEditClick}
              className="absolute right-4 p-2 rounded-full hover:bg-muted transition-colors"
            >
              <MoreHorizontal className="size-5 text-muted-foreground" />
            </button>
          )}
        </div>
        {openEditor && (
          <EditLessonModal
            open={openEditor}
            onClose={() => setOpenEditor(false)}
            lesson={lesson}
            refresh={refresh}
            snackbar={snackbar}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        onClick={handleClick}
        className="relative rounded-lg overflow-hidden border border-border bg-card shadow-sm cursor-pointer transition-all hover:shadow-md hover:-translate-y-1 hover:scale-[1.02] h-[30vh]"
      >
        <div
          style={thumbnailStyle}
          className="h-[65%] w-full bg-cover bg-center"
        />
        <div className="p-2.5 pr-9 mt-1 relative">
          <p className="font-semibold text-sm text-foreground line-clamp-1">
            {lesson.name}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {descriptionShort}
          </p>
        </div>
        {canManage && (
          <button
            onClick={handleEditClick}
            className={cn(
              'absolute top-2 right-2 p-1.5 rounded-full transition-colors',
              'hover:bg-black/20',
              lesson.imageType === 'pdf' && !lesson.posterImageName
                ? 'text-primary'
                : 'text-white'
            )}
          >
            <MoreHorizontal className="size-5" />
          </button>
        )}
      </div>
      {openEditor && (
        <EditLessonModal
          open={openEditor}
          onClose={() => setOpenEditor(false)}
          lesson={lesson}
          refresh={refresh}
          snackbar={snackbar}
        />
      )}
    </>
  );
}
